import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";

interface PositionWeight {
  ticker: string;
  totalValue: number;
}

interface Props {
  result: {
    tickers: string[];
    matrix: number[][];
    period: string;
    dataPoints: number;
  };
  positions?: PositionWeight[];
  sectorMap?: Record<string, string>;
}

import { getSectorColor } from "@/lib/sectorColors";

interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
  ticker: string;
  idx: number;
}

function edgeColor(corr: number): string {
  if (corr >= 0.7) return "hsl(0, 75%, 50%)";
  if (corr >= 0.4) return "hsl(25, 70%, 55%)";
  if (corr >= 0.1) return "hsl(45, 50%, 60%)";
  if (corr > -0.1) return "hsl(0, 0%, 75%)";
  if (corr > -0.4) return "hsl(210, 50%, 65%)";
  return "hsl(220, 70%, 50%)";
}

function edgeWidth(corr: number): number {
  return Math.max(0.5, Math.abs(corr) * 4);
}

export function CorrelationNetworkGraph({ result, positions, sectorMap }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const nodesRef = useRef<Node[]>([]);
  const [threshold, setThreshold] = useState([0.3]);
  const [hoveredNode, setHoveredNode] = useState<number | null>(null);
  const dragRef = useRef<{ idx: number; offsetX: number; offsetY: number } | null>(null);
  const isDark = document.documentElement.classList.contains("dark");

  const n = result.tickers.length;

  // Build weight map from positions
  const weightMap = useMemo(() => {
    if (!positions || positions.length === 0) return null;
    const totalPortfolio = positions.reduce((s, p) => s + Math.abs(p.totalValue || 0), 0);
    if (totalPortfolio === 0) return null;
    const map: Record<string, number> = {};
    for (const p of positions) {
      const pct = Math.abs(p.totalValue || 0) / totalPortfolio;
      // Aggregate if same ticker appears multiple times
      map[p.ticker] = (map[p.ticker] || 0) + pct;
    }
    return map;
  }, [positions]);

  // Initialize nodes in a circle
  useEffect(() => {
    const cx = 400, cy = 300, r = Math.min(200, 100 + n * 8);
    nodesRef.current = result.tickers.map((ticker, i) => ({
      x: cx + r * Math.cos((2 * Math.PI * i) / n - Math.PI / 2),
      y: cy + r * Math.sin((2 * Math.PI * i) / n - Math.PI / 2),
      vx: 0,
      vy: 0,
      ticker,
      idx: i,
    }));
  }, [result.tickers, n]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const nodes = nodesRef.current;
    const thresh = threshold[0];
    const textColor = isDark ? "#e2e8f0" : "#1a202c";
    const bgColor = isDark ? "#1a1a2e" : "#ffffff";

    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, w, h);

    // Force simulation step
    const cx = w / 2, cy = h / 2;
    for (const node of nodes) {
      // Center gravity
      node.vx += (cx - node.x) * 0.001;
      node.vy += (cy - node.y) * 0.001;

      // Repulsion from other nodes
      for (const other of nodes) {
        if (node.idx === other.idx) continue;
        const dx = node.x - other.x;
        const dy = node.y - other.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const force = 800 / (dist * dist);
        node.vx += (dx / dist) * force;
        node.vy += (dy / dist) * force;
      }

      // Attraction for correlated edges
      for (const other of nodes) {
        if (node.idx >= other.idx) continue;
        const corr = Math.abs(result.matrix[node.idx][other.idx]);
        if (corr < thresh) continue;
        const dx = other.x - node.x;
        const dy = other.y - node.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const force = (dist - 120) * corr * 0.002;
        node.vx += (dx / dist) * force;
        node.vy += (dy / dist) * force;
        other.vx -= (dx / dist) * force;
        other.vy -= (dy / dist) * force;
      }
    }

    for (const node of nodes) {
      if (dragRef.current?.idx === node.idx) continue;
      node.vx *= 0.85;
      node.vy *= 0.85;
      node.x += node.vx;
      node.y += node.vy;
      // Bounds
      node.x = Math.max(40, Math.min(w - 40, node.x));
      node.y = Math.max(40, Math.min(h - 40, node.y));
    }

    // Draw edges
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const corr = result.matrix[i][j];
        if (Math.abs(corr) < thresh) continue;
        const a = nodes[i], b = nodes[j];
        const isHighlighted = hoveredNode === i || hoveredNode === j;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.strokeStyle = edgeColor(corr);
        ctx.lineWidth = isHighlighted ? edgeWidth(corr) * 1.8 : edgeWidth(corr);
        ctx.globalAlpha = hoveredNode !== null ? (isHighlighted ? 0.9 : 0.15) : 0.6;
        ctx.stroke();
        ctx.globalAlpha = 1;

        // Label on edge if highlighted
        if (isHighlighted) {
          const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
          ctx.font = "bold 10px monospace";
          ctx.fillStyle = edgeColor(corr);
          ctx.textAlign = "center";
          ctx.fillText(corr.toFixed(2), mx, my - 4);
        }
      }
    }

    // Draw nodes — size by portfolio weight if available
    const baseRadius = Math.max(16, 28 - n * 0.5);
    const getNodeRadius = (ticker: string) => {
      if (!weightMap) return baseRadius;
      const w = weightMap[ticker] || 0;
      // Scale: min 12, max 40, based on weight (sqrt for visual area scaling)
      return Math.max(12, Math.min(40, baseRadius * 0.6 + Math.sqrt(w) * 80));
    };

    for (let i = 0; i < n; i++) {
      const node = nodes[i];
      const isHovered = hoveredNode === i;
      const nr = getNodeRadius(node.ticker);
      const r = isHovered ? nr * 1.2 : nr;

      // Glow
      if (isHovered) {
        ctx.beginPath();
        ctx.arc(node.x, node.y, r + 4, 0, Math.PI * 2);
        ctx.fillStyle = isDark ? "rgba(99,102,241,0.3)" : "rgba(59,130,246,0.25)";
        ctx.fill();
      }

      const sectorColors = getSectorColor(sectorMap?.[node.ticker], isDark);

      ctx.beginPath();
      ctx.arc(node.x, node.y, r, 0, Math.PI * 2);
      ctx.fillStyle = sectorColors.fill;
      ctx.fill();
      ctx.strokeStyle = sectorColors.stroke;
      ctx.lineWidth = isHovered ? 2.5 : 1.5;
      ctx.stroke();

      const fontSize = Math.max(9, Math.min(12, nr * 0.55));
      ctx.font = `${isHovered ? "bold " : ""}${fontSize}px monospace`;
      ctx.fillStyle = textColor;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(node.ticker, node.x, node.y);

      // Show weight % below ticker if weighted
      if (weightMap && weightMap[node.ticker]) {
        ctx.font = `${Math.max(7, fontSize - 2)}px monospace`;
        ctx.fillStyle = isDark ? "hsl(0, 0%, 60%)" : "hsl(0, 0%, 50%)";
        ctx.fillText(`${(weightMap[node.ticker] * 100).toFixed(1)}%`, node.x, node.y + fontSize * 0.8);
      }
    }

    animRef.current = requestAnimationFrame(draw);
  }, [result, threshold, hoveredNode, n, isDark, weightMap, sectorMap]);

  useEffect(() => {
    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [draw]);

  const getNodeAt = (ex: number, ey: number): number | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const x = ex - rect.left, y = ey - rect.top;
    const baseR = Math.max(16, 28 - n * 0.5);
    for (let i = nodesRef.current.length - 1; i >= 0; i--) {
      const nd = nodesRef.current[i];
      const nr = weightMap ? Math.max(12, Math.min(40, baseR * 0.6 + Math.sqrt(weightMap[nd.ticker] || 0) * 80)) : baseR;
      const dx = nd.x - x, dy = nd.y - y;
      if (dx * dx + dy * dy <= (nr + 4) * (nr + 4)) return i;
    }
    return null;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (dragRef.current) {
      const rect = canvasRef.current!.getBoundingClientRect();
      const node = nodesRef.current[dragRef.current.idx];
      node.x = e.clientX - rect.left - dragRef.current.offsetX;
      node.y = e.clientY - rect.top - dragRef.current.offsetY;
      node.vx = 0;
      node.vy = 0;
      return;
    }
    setHoveredNode(getNodeAt(e.clientX, e.clientY));
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const idx = getNodeAt(e.clientX, e.clientY);
    if (idx !== null) {
      const rect = canvasRef.current!.getBoundingClientRect();
      const node = nodesRef.current[idx];
      dragRef.current = {
        idx,
        offsetX: e.clientX - rect.left - node.x,
        offsetY: e.clientY - rect.top - node.y,
      };
    }
  };

  const handleMouseUp = () => {
    dragRef.current = null;
  };

  // Count visible edges
  const exportPng = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = "correlation-network.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  }, []);

  const edgeCount = (() => {
    let count = 0;
    for (let i = 0; i < n; i++)
      for (let j = i + 1; j < n; j++)
        if (Math.abs(result.matrix[i][j]) >= threshold[0]) count++;
    return count;
  })();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Correlation Network</CardTitle>
        <CardDescription>
          Drag nodes to rearrange · Hover to highlight connections · Edge color shows correlation strength
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-3 min-w-[280px]">
            <Label className="text-xs whitespace-nowrap">Min |corr|</Label>
            <Slider
              value={threshold}
              onValueChange={setThreshold}
              min={0}
              max={0.9}
              step={0.05}
              className="w-40"
            />
            <Badge variant="secondary" className="text-xs font-mono">{threshold[0].toFixed(2)}</Badge>
          </div>
          <Badge variant="outline" className="text-xs">
            {edgeCount} edges · {n} nodes
          </Badge>
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
            <span className="font-medium">Edges:</span>
            <div className="w-3 h-3 rounded-full" style={{ background: "hsl(220, 70%, 50%)" }} /> Neg
            <div className="w-3 h-3 rounded-full" style={{ background: "hsl(0, 0%, 75%)" }} /> Neutral
            <div className="w-3 h-3 rounded-full" style={{ background: "hsl(0, 75%, 50%)" }} /> Pos
          </div>
          {sectorMap && Object.keys(sectorMap).length > 0 && (() => {
            const sectors = Array.from(new Set(result.tickers.map(t => sectorMap[t]).filter(Boolean)));
            return (
              <div className="flex flex-wrap items-center gap-2 text-[10px] text-muted-foreground">
                <span className="font-medium">Sectors:</span>
                {sectors.map(s => {
                  const colors = getSectorColor(s, isDark);
                  return (
                    <div key={s} className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full border" style={{ background: colors.fill, borderColor: colors.stroke }} />
                      {s}
                    </div>
                  );
                })}
              </div>
            );
          })()}
          <Button variant="outline" size="sm" onClick={exportPng} className="ml-auto">
            <Download className="h-3.5 w-3.5 mr-1" /> PNG
          </Button>
        </div>
        <canvas
          ref={canvasRef}
          className="w-full rounded-lg border border-border cursor-grab active:cursor-grabbing"
          style={{ height: 600 }}
          onMouseMove={handleMouseMove}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={() => { dragRef.current = null; setHoveredNode(null); }}
        />
      </CardContent>
    </Card>
  );
}
